import { useDisclosure } from "@/hooks";
import { useGetRoomUnassignedMeditators } from "@/hooks/query-hooks/useMeditators";
import { useAssignMeditatorToRoom } from "@/hooks/query-hooks/useRoom";
import { useDebounce } from "@/hooks/useDebounce";
import { useNotificationStore } from "@/states/zustand/notification";
import {
	ExclamationCircleOutlined,
	PlusOutlined,
	SearchOutlined,
	TeamOutlined,
} from "@ant-design/icons";
import { Button, Divider, Modal, Select, Space } from "antd";
import { useEffect, useState } from "react";
import { RoomDrawerTitle } from "./RoomDrawerTitle";
import { UserCard } from "./UserCard";

export const AssignUserModal = ({ room }: { room: Room }) => {
	const [open, { toggle }] = useDisclosure();
	const [keyword, setKeyword] = useState<string | null>(null);
	const [modal, contextHolder] = Modal.useModal();
	const debouncedValue = useDebounce(keyword, 500);

	const { data: meditators = [], isLoading } = useGetRoomUnassignedMeditators(
		room.meditationEvent?.id as number,
		open,
	);
	const meditatorOptions = meditators?.map((it) => ({
		label: `${it.name} ${it.book?.bookNo ? "(" + it.book.bookNo + ")" : ""}`,
		value: JSON.stringify(it),
		data: it,
	}));

	useEffect(() => {
		if (
			debouncedValue &&
			debouncedValue.trim() &&
			meditators?.length === 0
		) {
			useNotificationStore.getState().setNotification({
				type: "error",
				message: `No user found with ${debouncedValue} in ${room.meditationEvent?.title}!`,
			});
		}
	}, [debouncedValue, meditators, room]);

	const { mutate } = useAssignMeditatorToRoom();
	const onChange = (value: string) => {
		const user = JSON.parse(value) as Meditator;
		modal.confirm({
			icon: <ExclamationCircleOutlined />,
			title: (
				<h2 className="font-poppins text-primary-gradient">
					Add {user.name} to {room.name} room?
				</h2>
			),
			content: (
				<p className="font-poppins">
					Are you sure you want to add {user.name}{" "}
					{user?.book ? `(${user?.book.bookNo})` : null} to room name{" "}
					{room.name}?
				</p>
			),
			okText: "Confirm",
			okButtonProps: {
				type: "primary",
				className: " bg-primary-gradient rounded-lg!",
				danger: true,
			},
			cancelButtonProps: {
				type: "text",
			},
			onOk: () => {
				return new Promise((resolve, reject) => {
					mutate(
						{ roomId: room.id, userId: user.id },
						{
							onSuccess: () => resolve(setKeyword(null)),
							onError: reject,
						},
					);
				});
			},
		});
	};

	return (
		<>
			<Button
				type="primary"
				size="large"
				shape="circle"
				icon={<PlusOutlined />}
				className="bg-primary-gradient"
				title="Add People"
				onClick={toggle}
			/>
			<Modal
				open={open}
				onCancel={toggle}
				title={<RoomDrawerTitle room={room} onClose={toggle} />}
				footer={null}
				closeIcon={null}
				maskClosable={false}
			>
				{contextHolder}
				<Divider
					style={{
						marginBlock: 12,
					}}
				/>
				<Space direction="vertical" className="w-full! pb-2">
					<label className="font-poppins" htmlFor="search-people">
						Search and add people
					</label>
					<Select
						id="search-people"
						placeholder={"Search and add people"}
						prefix={
							<SearchOutlined className="text-base! font-semibold bg-white! text-gray-400!" />
						}
						onSearch={(value) => setKeyword(value)}
						showSearch
						options={meditatorOptions}
						loading={isLoading}
						size="large"
						style={{
							width: "100%",
						}}
						notFoundContent={
							<Space
								direction="vertical"
								className="w-full! py-4"
								align="center"
							>
								<TeamOutlined className="text-5xl!" />
								<p>No user found!</p>
							</Space>
						}
						// value={keyword}
						onChange={onChange}
						autoClearSearchValue
						optionRender={({ data: { data } }, { index }) => {
							const roomReqUser: RoomRequestUser = {
								userId: data.id,
								userName: data.name,
								bookNo: data.book?.bookNo,
								id: data.id,
								email: data?.email as string | undefined,
								status: "",
								phone: data?.phone,
								eventId: room.meditationEvent?.id as number,
							};

							return (
								<UserCard
									key={data.id}
									user={roomReqUser}
									index={index + 1}
									containerClassName="pb-0.5 pt-0.5 border-none"
									isInSelectScroll={true}
								/>
							);
						}}
					/>
					<p className="text-sm text-gray-400 font-poppins">
						Search for a meditator by their name or book number to
						quickly add them to this room.
					</p>
				</Space>
			</Modal>
		</>
	);
};
